import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  Spinner
} from 'reactstrap';
import axios from 'axios';
import { baseURL } from '../../../../Services/api/baseURL';
import { toast } from 'react-toastify';
import { TrendingUp, DollarSign, Mail, ShoppingBag, MousePointer, Eye } from 'react-feather';
import DataTable from 'react-data-table-component';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const EmailMarketingDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [revenueData, setRevenueData] = useState(null);
  const [timePeriod, setTimePeriod] = useState('day');
  const [activeTab, setActiveTab] = useState('30days');

  // Fetch email marketing metrics
  const fetchMetrics = async (sinceDays = null) => {
    try {
      const token = JSON.parse(localStorage.getItem('token'));
      const params = {};

      if (sinceDays) {
        const sinceDate = new Date();
        sinceDate.setDate(sinceDate.getDate() - sinceDays);
        params.since_send_time = sinceDate.toISOString();
      }

      const response = await axios.get(`${baseURL}/api/mailchimp/metrics`, {
        headers: { Authorization: token },
        params
      });

      if (response.data.success) {
        setMetrics(response.data.data.metrics);
        setCampaigns(response.data.data.topCampaigns || []);
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
      toast.error('Failed to fetch email marketing metrics');
    }
  };

  // Fetch revenue timeline data
  const fetchRevenueTimeline = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('token'));
      const response = await axios.get(`${baseURL}/api/mailchimp/revenue/timeline`, {
        headers: { Authorization: token },
        params: { period: timePeriod, count: 30 }
      });

      if (response.data.success) {
        setRevenueData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching revenue timeline:', error);
      toast.error('Failed to fetch revenue timeline');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const days = activeTab === '30days' ? 30 : activeTab === '90days' ? 90 : null;
      await Promise.all([
        fetchMetrics(days),
        fetchRevenueTimeline()
      ]);
      setLoading(false);
    };

    loadData();
  }, [activeTab, timePeriod]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  // Format number
  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-AU').format(num || 0);
  };

  // Format percentage
  const formatPercent = (percent) => {
    return `${(percent || 0).toFixed(2)}%`;
  };

  // Metric cards data
  const metricCards = metrics ? [
    {
      title: 'Total Revenue',
      value: formatCurrency(metrics.totalRevenue),
      icon: DollarSign,
      color: '#28a745',
      bgColor: '#d4edda'
    },
    {
      title: 'Total Orders',
      value: formatNumber(metrics.totalOrders),
      icon: ShoppingBag,
      color: '#007bff',
      bgColor: '#cfe2ff'
    },
    {
      title: 'Average Order Value',
      value: formatCurrency(metrics.averageOrderValue),
      icon: TrendingUp,
      color: '#ffc107',
      bgColor: '#fff3cd'
    },
    {
      title: 'Emails Sent',
      value: formatNumber(metrics.totalEmailsSent),
      icon: Mail,
      color: '#6c757d',
      bgColor: '#e2e3e5'
    },
    {
      title: 'Open Rate',
      value: formatPercent(metrics.overallOpenRate),
      icon: Eye,
      color: '#17a2b8',
      bgColor: '#d1ecf1'
    },
    {
      title: 'Click Rate',
      value: formatPercent(metrics.overallClickRate),
      icon: MousePointer,
      color: '#dc3545',
      bgColor: '#f8d7da'
    }
  ] : [];

  // Chart data
  const chartData = revenueData ? {
    labels: revenueData.data.map(d => d.period),
    datasets: [
      {
        label: 'Revenue',
        data: revenueData.data.map(d => parseFloat(d.revenue.toFixed(2))),
        borderColor: '#28a745',
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Orders',
        data: revenueData.data.map(d => d.orders),
        borderColor: '#007bff',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y1'
      }
    ]
  } : null;

  const chartOptions = {
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Email Marketing Revenue Over Time'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.dataset.label === 'Revenue') {
              label += formatCurrency(context.parsed.y);
            } else {
              label += context.parsed.y;
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Revenue (AUD)'
        },
        ticks: {
          callback: function(value) {
            return '$' + value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          }
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Orders'
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          stepSize: 1,
          callback: function(value) {
            if (Number.isInteger(value)) {
              return value;
            }
          }
        },
        beginAtZero: true
      },
    },
  };

  // Function to open campaign in Mailchimp dashboard
  const openCampaignInMailchimp = (campaignId, webId) => {
    const serverPrefix = 'us9'; // This should match your MAILCHIMP_SERVER_PREFIX
    // Use web_id if available, otherwise fallback to id
    const campaignIdentifier = webId || campaignId;
    const mailchimpUrl = `https://${serverPrefix}.admin.mailchimp.com/campaigns/show?id=${campaignIdentifier}`;
    window.open(mailchimpUrl, '_blank');
  };

  // Campaign table columns
  const campaignColumns = [
    {
      name: 'Campaign',
      selector: row => row.title,
      sortable: true,
      minWidth: '250px',
      cell: row => (
        <div
          onClick={() => openCampaignInMailchimp(row.id, row.web_id)}
          style={{
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          className="campaign-link"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateX(5px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateX(0)';
          }}
          title="Click to view in Mailchimp"
        >
          <div className="fw-bold text-primary" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            {row.title}
            <i className="bi bi-box-arrow-up-right" style={{ fontSize: '12px' }}></i>
          </div>
          <div className="text-muted small">{row.subject_line}</div>
        </div>
      )
    },
    {
      name: 'Sent Date',
      selector: row => row.send_time,
      sortable: true,
      cell: row => new Date(row.send_time).toLocaleDateString()
    },
    {
      name: 'Revenue',
      selector: row => row.total_revenue,
      sortable: true,
      cell: row => (
        <span className="fw-bold text-success">
          {formatCurrency(row.total_revenue)}
        </span>
      )
    },
    {
      name: 'Orders',
      selector: row => row.total_orders,
      sortable: true,
      cell: row => formatNumber(row.total_orders)
    },
    {
      name: 'Open Rate',
      selector: row => row.open_rate,
      sortable: true,
      cell: row => formatPercent(row.open_rate * 100)
    },
    {
      name: 'Click Rate',
      selector: row => row.click_rate,
      sortable: true,
      cell: row => formatPercent(row.click_rate * 100)
    },
    {
      name: 'Emails Sent',
      selector: row => row.emails_sent,
      sortable: true,
      cell: row => formatNumber(row.emails_sent)
    }
  ];

  if (loading) {
    return (
      <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner color="primary" />
      </Container>
    );
  }

  return (
    <Container fluid className="email-marketing-dashboard">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h5 className="fw-bold my-3">Email Marketing Dashboard</h5>         
          <Nav tabs>
            <NavItem>
              <NavLink
                className={activeTab === '30days' ? 'active' : ''}
                onClick={() => setActiveTab('30days')}
                style={{ cursor: 'pointer' }}
              >
                Last 30 Days
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === '90days' ? 'active' : ''}
                onClick={() => setActiveTab('90days')}
                style={{ cursor: 'pointer' }}
              >
                Last 90 Days
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'all' ? 'active' : ''}
                onClick={() => setActiveTab('all')}
                style={{ cursor: 'pointer' }}
              >
                All Time
              </NavLink>
            </NavItem>
          </Nav>
        </Col>
      </Row>

      {/* Metric Cards */}
      <Row className="mb-4">
        {metricCards.map((metric, index) => (
          <Col md={4} lg={2} key={index} className="mb-3">
            <Card className="shadow-sm border-0 h-100">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div
                    className="rounded-circle p-2"
                    style={{ backgroundColor: metric.bgColor }}
                  >
                    <metric.icon size={20} color={metric.color} />
                  </div>
                </div>
                <h6 className="text-muted small mb-1">{metric.title}</h6>
                <h4 className="fw-bold mb-0">{metric.value}</h4>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Revenue Chart */}
      {chartData && (
        <Row className="mb-4">
          <Col>
            <Card className="shadow-sm border-0">
              <CardHeader className="bg-white">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-bold">Revenue Timeline</h5>
                  <div className="btn-group btn-group-sm">
                    <button
                      className={`btn ${timePeriod === 'day' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setTimePeriod('day')}
                    >
                      Daily
                    </button>
                    <button
                      className={`btn ${timePeriod === 'week' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setTimePeriod('week')}
                    >
                      Weekly
                    </button>
                    <button
                      className={`btn ${timePeriod === 'month' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setTimePeriod('month')}
                    >
                      Monthly
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <Line data={chartData} options={chartOptions} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Top Campaigns Table */}
      <Row>
        <Col>
          <Card className="shadow-sm border-0">
            <CardHeader className="bg-white">
              <h5 className="mb-0 fw-bold">Top Performing Campaigns</h5>
            </CardHeader>
            <CardBody>
              <DataTable
                columns={campaignColumns}
                data={campaigns}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 30, 50]}
                highlightOnHover
                striped
                responsive
                noDataComponent={
                  <div className="text-center py-5">
                    <p className="text-muted">No campaigns found</p>
                  </div>
                }
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EmailMarketingDashboard;
